import { useCallback, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDocumentMeta } from '../i18n/useDocumentMeta'
import { Icon } from '../components/Icon'
import { Stars } from '../components/Stars'
import { HeroScene } from '../components/HeroScene'
import { FeatRow } from '../components/FeatRow'
import { useCatalog } from '../hooks/useCatalog'
import { useCountriesMeta } from '../hooks/useCountriesMeta'
import {
  BUNDLE_SEARCH_COUNTRIES,
  COUNTRIES,
  REGIONAL_PLANS_META,
  REGION_COVERAGE,
  REVIEWS,
  fromPrice,
  priceDollars,
} from '../data/catalog'
import { languageFromPath, withLang } from '../i18n/i18n'

type FeatTab = 'local' | 'regional'

export default function Landing() {
  const { plans } = useCatalog()
  const countriesMeta = useCountriesMeta()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('landing')
  const { t: tCommon } = useTranslation('common')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })
  const [q, setQ] = useState('')

  // Country/region names translated through common.{countries,regions}.
  // Fallback to the catalog name if no translation key exists yet.
  const countryName = useCallback(
    (code: string, fallback: string) => {
      const v = tCommon(`countries.${code}`)
      return v === `countries.${code}` ? fallback : v
    },
    [tCommon],
  )
  const regionName = useCallback(
    (code: string, fallback: string) => {
      const v = tCommon(`regions.${code}`)
      return v === `regions.${code}` ? fallback : v
    },
    [tCommon],
  )

  // Hero search suggestions: matching countries first, then regional
  // bundles that match by name OR cover the typed country (via
  // REGION_COVERAGE + BUNDLE_SEARCH_COUNTRIES) — so "Malaysia" also
  // surfaces the 4-country bundle, and "Brazil" finds South America
  // even though there's no standalone Brazil page.
  const suggestions = useMemo(() => {
    if (!q.trim()) return []
    const lower = q.toLowerCase()
    // Rank by how well the name matches, not by catalog position. A plain
    // `includes` in catalog order buries the obvious answer: typing "u" put
    // Uruguay 7th — behind South Korea, Turkey and Peru, which merely have a
    // "u" in the middle — so it fell outside the 6 we show and looked like
    // we didn't sell it. Lower score wins; ties keep catalog order because
    // Array.prototype.sort is stable.
    const score = (name: string) => {
      const n = name.toLowerCase()
      if (n === lower) return 0
      if (n.startsWith(lower)) return 1
      // A later word starting with the query: "arab" -> United Arab Emirates.
      if (n.split(/[\s-]+/).some((w) => w.startsWith(lower))) return 2
      return n.includes(lower) ? 3 : Infinity
    }
    // Match against both the source-of-truth English name AND the translated
    // name so /zh users can type Chinese (e.g. "日本" finds Japan). Score the
    // better of the two, so ranking works in every locale.
    const countries = COUNTRIES.map((c) => {
      const s = Math.min(score(c.name), score(countryName(c.code, c.name)))
      return { s, code: c.code, flag: c.flag, label: countryName(c.code, c.name) }
    }).filter((x) => x.s !== Infinity)
    const bundles = REGIONAL_PLANS_META.map((r) => {
      let s = Math.min(score(r.name), score(regionName(r.code, r.name)))
      if (s === Infinity) {
        // Bundles also match on what they cover, so "Brazil" finds South
        // America. A covered-country hit ranks below any name hit.
        const covered = [
          ...(REGION_COVERAGE[r.code]?.map((e) => e.name) ?? []),
          ...(BUNDLE_SEARCH_COUNTRIES[r.code] ?? []),
        ]
        if (covered.some((n) => score(n) !== Infinity)) s = 4
      }
      return { s, code: r.code, flag: r.icon, label: regionName(r.code, r.name) }
    }).filter((x) => x.s !== Infinity)
    // Countries stay ahead of bundles as a group — a standalone destination
    // page is the more direct answer — and each group is ranked internally.
    const byScore = (a: { s: number }, b: { s: number }) => a.s - b.s
    return [...countries.sort(byScore), ...bundles.sort(byScore)]
      .slice(0, 6)
      .map(({ code, flag, label }) => ({ code, flag, label }))
  }, [q, countryName, regionName])

  // Surface China first on the front page (our core market); keep the rest in
  // catalog order. Only reorders this render — the Destinations page is unaffected.
  const popularCountries = useMemo(() => {
    const cn = COUNTRIES.filter((c) => c.popular && c.code === 'CN')
    const rest = COUNTRIES.filter((c) => c.popular && c.code !== 'CN')
    return [...cn, ...rest]
  }, [])
  // Regional bundles for the front page: surface the NAM (USA+Canada+Mexico)
  // bundle first, keep the rest in catalog order. Only reorders this render —
  // the Destinations page (alphabetical) and footer are unaffected.
  const regionalFeatured = useMemo(() => {
    const nam = REGIONAL_PLANS_META.filter((r) => r.code === 'NAM')
    const rest = REGIONAL_PLANS_META.filter((r) => r.code !== 'NAM')
    return [...nam, ...rest]
  }, [])
  const [featTab, setFeatTab] = useState<FeatTab>('local')

  const goTo = (code: string) => navigate(withLang(`/destinations/${code.toLowerCase()}`, lang))
  const priceFor = (code: string) => (plans ? fromPrice(plans, code) : null)

  return (
    <>
      {/* HERO — full-bleed landmark scene (shared design lifted from the
          old How-it-works header). Centered copy + search + key stats. */}
      <section className="how-hero">
        <div className="how-hero-bg">
          <HeroScene />
        </div>
        <div className="how-hero-overlay" />
        <div className="how-hero-inner">
          <div className="eyebrow how-hero-eyebrow">{t('hero.eyebrow')}</div>
          <h1 className="how-hero-display">
            {t('hero.titleLine1')}<em>{t('hero.titleEmphasis')}</em>
            <br />
            {t('hero.titleLine2')}
          </h1>
          <p className="how-hero-sub">
            <strong className="how-hero-sub-key">{t('hero.subKey1')}</strong>{t('hero.subJoin')}<strong className="how-hero-sub-key">{t('hero.subKey2')}</strong>{t('hero.subTail')}
            <Link className="how-hero-sub-link" to={withLang('/refund-policy', lang)}>{t('hero.guarantee')}</Link>.
          </p>

          <div className="how-hero-search">
            <div className="how-hero-search-icon">
              <Icon name="search" size={18} />
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('hero.searchPlaceholder')}
              aria-label={t('hero.searchPlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  navigate(
                    suggestions[0]
                      ? withLang(`/destinations/${suggestions[0].code.toLowerCase()}`, lang)
                      : withLang('/destinations', lang),
                  )
                }
              }}
            />
            <button
              type="button"
              className="btn primary how-hero-search-btn"
              onClick={() =>
                navigate(
                  suggestions[0]
                    ? withLang(`/destinations/${suggestions[0].code.toLowerCase()}`, lang)
                    : withLang('/destinations', lang),
                )
              }
            >
              {t('hero.searchButton')}
            </button>
            {suggestions.length > 0 && (
              <div className="how-hero-suggestions">
                {suggestions.map((s) => {
                  const fromCents = priceFor(s.code)
                  return (
                    <button
                      key={s.code}
                      type="button"
                      className="how-hero-suggestion"
                      onClick={() => goTo(s.code)}
                    >
                      <span className="how-hero-suggestion-flag">{s.flag}</span>
                      <span className="how-hero-suggestion-name">{s.label}</span>
                      {fromCents !== null && (
                        <span className="how-hero-suggestion-price mono">
                          {t('hero.from')} ${priceDollars(fromCents)}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="how-hero-stats">
            <div className="how-hero-stat">
              <div className="num">{t('hero.stats.countriesNumber')}</div>
              <div className="lbl">{t('hero.stats.countriesLabel')}</div>
            </div>
            <div className="how-hero-stat">
              <div className="num">
                {t('hero.stats.installNumber')}
                <span className="unit">{t('hero.stats.installUnit')}</span>
              </div>
              <div className="lbl">{t('hero.stats.installLabel')}</div>
            </div>
            <div className="how-hero-stat">
              <div className="num">
                {t('hero.stats.creditNumber')}
                <span className="unit">{t('hero.stats.creditUnit')}</span>
              </div>
              <div className="lbl">{t('hero.stats.creditLabel')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED DESTINATIONS */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="section-inner">
          <div className="section-head">
            <div>
              <div className="eyebrow" style={{ marginBottom: 14 }}>
                {t('popular.eyebrow')}
              </div>
              <h2 className="h1">
                {t('popular.titlePart1')}
                <em style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 400 }}>
                  {t('popular.titleEmphasis')}
                </em>
                {t('popular.titleSuffix')}
              </h2>
            </div>
          </div>

          <div className="plan-tabs" style={{ marginBottom: 28 }}>
            <button
              className={featTab === 'local' ? 'active' : ''}
              onClick={() => setFeatTab('local')}
            >
              <span className="dot"></span> {t('popular.tabLocal')}
            </button>
            <button
              className={featTab === 'regional' ? 'active' : ''}
              onClick={() => setFeatTab('regional')}
            >
              <span className="dot"></span> {t('popular.tabRegional')}
            </button>
          </div>

          {featTab === 'local' && (
            <div className="feat-list">
              {popularCountries.map((c) => (
                <FeatRow
                  key={c.code}
                  flag={c.flag}
                  name={countryName(c.code, c.name)}
                  sub={(countriesMeta?.[c.code]?.networks ?? c.networks).split(',')[0]}
                  fromCents={priceFor(c.code)}
                  fromLabel={t('popular.from')}
                  currency={t('popular.currency')}
                  onClick={() => goTo(c.code)}
                />
              ))}
            </div>
          )}

          {featTab === 'regional' && (
            <div className="feat-list">
              {regionalFeatured.map((r) => (
                <FeatRow
                  key={r.code}
                  flag={r.icon}
                  name={regionName(r.code, r.name)}
                  sub={r.scope}
                  fromCents={priceFor(r.code)}
                  fromLabel={t('popular.from')}
                  currency={t('popular.currency')}
                  onClick={() => goTo(r.code)}
                />
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 36 }}>
            <button
              className="btn ghost lg"
              onClick={() => navigate(withLang('/destinations', lang))}
              style={{ minWidth: 280 }}
            >
              {t('popular.viewAll')} <Icon name="arrow" size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="section" style={{ background: 'var(--bg-sunk)' }}>
        <div className="section-inner">
          <div className="section-head">
            <div>
              <div className="eyebrow" style={{ marginBottom: 14 }}>
                {t('how.eyebrow')}
              </div>
              <h2 className="h1">{t('how.title')}</h2>
            </div>
          </div>

          <div className="how">
            <div className="how-step">
              <span className="n">{t('how.step1.label')}</span>
              <h3>{t('how.step1.title')}</h3>
              <p>{t('how.step1.body')}</p>
              <div className="art">
                <div style={{ display: 'flex', gap: 6 }}>
                  <span className="chip active">🇯🇵 Japan</span>
                  <span className="chip">🇺🇸 USA</span>
                  <span className="chip">🌍 Europe</span>
                </div>
              </div>
            </div>
            <div className="how-step">
              <span className="n">{t('how.step2.label')}</span>
              <h3>{t('how.step2.title')}</h3>
              <p>{t('how.step2.body')}</p>
              <div className="art" style={{ flexDirection: 'column', gap: 8, padding: 14 }}>
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <div
                    style={{
                      flex: 1,
                      height: 32,
                      borderRadius: 6,
                      background: 'var(--bg-elev)',
                      border: '1px solid var(--line)',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 10px',
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                    }}
                  >
                    •••• 4242
                  </div>
                  <div
                    style={{
                      width: 60,
                      height: 32,
                      borderRadius: 6,
                      background: 'var(--ink)',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'var(--bg)',
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                    }}
                  >
                    PAY
                  </div>
                </div>
              </div>
            </div>
            <div className="how-step">
              <span className="n">{t('how.step3.label')}</span>
              <h3>{t('how.step3.title')}</h3>
              <p>{t('how.step3.body')}</p>
              <div className="art art--install">
                <span className="install-mock-btn">
                  <Icon name="check" size={11} /> {t('how.step3.installBtn')}
                </span>
                <span className="install-mock-or">{t('how.step3.or')}</span>
                <svg width="60" height="60" viewBox="0 0 72 72" aria-hidden="true" className="install-mock-qr">
                  {Array.from({ length: 9 }).flatMap((_, y) =>
                    Array.from({ length: 9 }).map((_, x) => {
                      const v = (x * 7 + y * 13 + x * y) % 3
                      return v !== 0 ? (
                        <rect
                          key={`${x},${y}`}
                          x={x * 8}
                          y={y * 8}
                          width="7"
                          height="7"
                          fill="var(--ink)"
                        />
                      ) : null
                    }),
                  )}
                  <rect x="0" y="0" width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="4" />
                  <rect x="52" y="0" width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="4" />
                  <rect x="0" y="52" width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="4" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIT — brand moment for the 10%-back program. Full-width
          navy gradient breaks the cream rhythm of other sections; same
          visual language as the credit strip on /account so customers
          recognize the program when they sign in. Editorial centered
          layout: small eyebrow, italic-serif headline (matches the
          destination accent type), body copy, single CTA. */}
      <section className="section credit-promo">
        <div className="credit-promo__glow" aria-hidden="true" />
        <div className="credit-promo__inner">
          <div className="credit-promo__eyebrow">{t('credit.eyebrow')}</div>
          <h2 className="credit-promo__headline">
            {t('credit.titlePart1')}
            <em
              style={{
                fontFamily: 'var(--serif)',
                fontStyle: 'italic',
                fontWeight: 400,
              }}
            >
              {t('credit.titleEmphasis')}
            </em>
          </h2>
          <p className="credit-promo__copy">{t('credit.body')}</p>
          <div className="credit-promo__row">
            <span className="credit-promo__chip">
              <span className="credit-promo__chip-amt">$20</span>
              <span className="credit-promo__chip-arrow">→</span>
              <span className="credit-promo__chip-earn">+$2</span>
            </span>
            <span className="credit-promo__chip">
              <span className="credit-promo__chip-amt">$50</span>
              <span className="credit-promo__chip-arrow">→</span>
              <span className="credit-promo__chip-earn">+$5</span>
            </span>
            <span className="credit-promo__chip">
              <span className="credit-promo__chip-amt">$100</span>
              <span className="credit-promo__chip-arrow">→</span>
              <span className="credit-promo__chip-earn">+$10</span>
            </span>
          </div>
          <div className="credit-promo__cta-row">
            <button
              className="btn lg block credit-promo__cta"
              onClick={() => navigate(withLang('/destinations', lang))}
            >
              {t('credit.cta')} <Icon name="arrow" size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="section-inner">
          <div className="section-head">
            <div>
              <div className="eyebrow" style={{ marginBottom: 14 }}>
                {t('reviews.eyebrow')}
              </div>
              <h2 className="h1">
                {t('reviews.titleLine1')}
                <br />
                {t('reviews.titleLine2')}
              </h2>
            </div>
          </div>

          <div className="reviews">
            {REVIEWS.map((r, i) => (
              <div key={i} className="review">
                <div className="stars">
                  <Stars n={r.stars} />
                </div>
                <p>"{r.text}"</p>
                <div className="who">
                  <div className="av">
                    {r.name
                      .split(' ')
                      .map((p) => p[0])
                      .join('')}
                  </div>
                  <div>
                    <strong style={{ fontWeight: 500 }}>{r.name}</strong>
                    <div
                      className="muted mono"
                      style={{ fontSize: 11.5, letterSpacing: '0.04em' }}
                    >
                      {r.from}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </>
  )
}

